// Upload form for a new hosted site — required description, optional AI summary, .html or .zip file

import { useId, useRef, useState, type FormEvent } from 'react'
import { toast } from 'sonner'
import { Upload } from 'lucide-react'
import { useUploadSite } from '../../hooks/use-hosted-sites'
import { useT } from '../../i18n'
import { cn } from '../../lib/utils'
import { buttonPrimaryClass, buttonSecondaryClass, cardClass, errorTextClass, hintClass, inputClass, labelClass } from '../ui/styles'
import { DESCRIPTION_MAX } from './utils'

interface UploadPanelProps {
  onClose: () => void
}

export function UploadPanel({ onClose }: UploadPanelProps) {
  const t = useT()
  const id = useId()
  const uploadSite = useUploadSite()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [description, setDescription] = useState('')
  const [autoSummary, setAutoSummary] = useState(false)
  const [file, setFile] = useState<File | null>(null)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    const trimmed = description.trim()
    if (!trimmed) {
      setError(t('sites.upload.needDescription'))
      return
    }
    if (!file) {
      setError(t('sites.upload.needFile'))
      return
    }
    setError(null)
    try {
      const result = await uploadSite.mutateAsync({ file, description: trimmed, autoSummary: autoSummary || undefined })
      toast.success(t('sites.upload.done', { name: result.filename }))
      setDescription('')
      setAutoSummary(false)
      setFile(null)
      if (fileInputRef.current) fileInputRef.current.value = ''
      onClose()
    } catch (err) {
      setError(t('sites.upload.failed', { message: err instanceof Error ? err.message : String(err) }))
    }
  }

  return (
    <form onSubmit={handleSubmit} noValidate aria-labelledby={`${id}-title`} className={cn(cardClass, 'space-y-4 p-4')}>
      <h2 id={`${id}-title`} className="text-sm font-semibold text-fg">
        {t('sites.upload.title')}
      </h2>
      <div className="space-y-1.5">
        <label htmlFor={`${id}-description`} className={labelClass}>
          {t('sites.upload.description')}
        </label>
        <input
          id={`${id}-description`}
          type="text"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={DESCRIPTION_MAX}
          required
          placeholder={t('sites.upload.descriptionPlaceholder')}
          className={inputClass}
        />
        <p className={hintClass}>{t('sites.upload.descriptionHint', { max: DESCRIPTION_MAX })}</p>
      </div>
      <div className="space-y-1.5">
        <label htmlFor={`${id}-file`} className={labelClass}>
          {t('sites.upload.file')}
        </label>
        <input
          id={`${id}-file`}
          ref={fileInputRef}
          type="file"
          accept=".html,.htm,.zip"
          required
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          className="block w-full cursor-pointer text-sm text-fg-muted file:mr-3 file:cursor-pointer file:rounded-md file:border file:border-border file:bg-surface file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-fg hover:file:bg-surface-hover"
        />
        <p className={hintClass}>{t('sites.upload.fileHint')}</p>
      </div>
      <label className="flex min-h-8 cursor-pointer items-center gap-2 text-sm text-fg-muted">
        <input type="checkbox" checked={autoSummary} onChange={(e) => setAutoSummary(e.target.checked)} className="h-4 w-4 rounded accent-accent" />
        {t('sites.upload.autoSummary')}
      </label>
      {error && (
        <p role="alert" className={errorTextClass}>
          {error}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <button type="submit" disabled={uploadSite.isPending} className={buttonPrimaryClass}>
          <Upload className="h-4 w-4" aria-hidden="true" />
          {uploadSite.isPending ? t('sites.upload.uploading') : t('sites.upload.submit')}
        </button>
        <button type="button" onClick={onClose} className={buttonSecondaryClass}>
          {t('common.cancel')}
        </button>
      </div>
    </form>
  )
}
